import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import aboutData from '../data/about.json';
import contactData from '../data/contact.json';
import eventsData from '../data/events.json';
import featuredData from '../data/featured.json';
import galleryData from '../data/gallery.json';
import homeData from '../data/home.json';
import notificationsData from '../data/notifications.json';
import teamData from '../data/team.json';
import workshopsData from '../data/workshops.json';
import { isSupabaseConfigured, supabase } from '../lib/supabase';

export type ContentSectionKey =
  | 'about'
  | 'contact'
  | 'events'
  | 'featured'
  | 'gallery'
  | 'home'
  | 'notifications'
  | 'team'
  | 'workshops';

type ContentSectionsRecord = Record<ContentSectionKey, unknown>;

type ContentContextValue = {
  sections: ContentSectionsRecord;
  loading: boolean;
  hasRemote: boolean;
  refreshContent: () => Promise<void>;
  getSection: <T>(key: ContentSectionKey, fallback?: T) => T;
};

const defaultSections: ContentSectionsRecord = {
  about: aboutData,
  contact: contactData,
  events: eventsData,
  featured: featuredData,
  gallery: galleryData,
  home: homeData,
  notifications: notificationsData,
  team: teamData,
  workshops: workshopsData
};

const ContentContext = createContext<ContentContextValue | undefined>(undefined);

export const ContentProvider = ({ children }: { children: React.ReactNode }) => {
  const [sections, setSections] = useState<ContentSectionsRecord>(defaultSections);
  const [loading, setLoading] = useState<boolean>(false);

  const refreshContent = useCallback(async () => {
    if (!isSupabaseConfigured || !supabase) {
      setSections(defaultSections);
      return;
    }

    setLoading(true);

    const { data, error } = await supabase
      .from('content_sections')
      .select('section_key, content, is_published')
      .eq('is_published', true);

    if (error || !data) {
      setLoading(false);
      return;
    }

    const merged: ContentSectionsRecord = { ...defaultSections };

    data.forEach((row) => {
      const key = row.section_key as ContentSectionKey;
      if (key in merged && row.content) {
        merged[key] = row.content;
      }
    });

    setSections(merged);
    setLoading(false);
  }, []);

  useEffect(() => {
    refreshContent();
  }, [refreshContent]);

  const getSection = useCallback(
    <T,>(key: ContentSectionKey, fallback?: T): T => {
      const value = sections[key];
      if (value !== undefined && value !== null) {
        return value as T;
      }
      return (fallback !== undefined ? fallback : defaultSections[key]) as T;
    },
    [sections]
  );

  const value = useMemo(
    () => ({
      sections,
      loading,
      hasRemote: isSupabaseConfigured,
      refreshContent,
      getSection
    }),
    [sections, loading, refreshContent, getSection]
  );

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
};

export const useContent = () => {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
};
